const DEFAULT_API_BASE = String(process.env.REACT_APP_API_BASE || "").trim();

function safeTrim(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function buildWindowOrigin() {
  if (typeof window === "undefined" || !window.location) return "";
  return `${window.location.protocol}//${window.location.host}`.replace(/\/$/, "");
}

function joinUrl(base, pathname) {
  const normalizedBase = safeTrim(base).replace(/\/+$/, "");
  const normalizedPath = safeTrim(pathname);

  if (!normalizedBase) {
    return normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
  }

  if (!normalizedPath) return normalizedBase;
  return `${normalizedBase}${normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`}`;
}

function dedupeUrls(values = []) {
  return Array.from(
    new Set(
      values
        .map((value) => safeTrim(value))
        .filter(Boolean)
    )
  );
}

function extractUploadsPath(value) {
  const raw = safeTrim(value);
  if (!raw) return "";

  const normalizeUploadsPath = (input) => {
    const candidate = safeTrim(input);
    if (!candidate) return "";
    if (candidate.startsWith("/uploads/")) return candidate;
    if (candidate.startsWith("uploads/")) return `/${candidate}`;

    const markerIndex = candidate.indexOf("/uploads/");
    return markerIndex >= 0 ? candidate.slice(markerIndex) : "";
  };

  if (raw.startsWith("//")) {
    const protocol = typeof window !== "undefined" && window.location?.protocol
      ? window.location.protocol
      : "https:";
    return extractUploadsPath(`${protocol}${raw}`);
  }

  const directMatch = normalizeUploadsPath(raw);
  if (directMatch) return directMatch;

  try {
    const parsed = new URL(raw);
    const rebuilt = `${parsed.pathname || ""}${parsed.search || ""}${parsed.hash || ""}`;
    return normalizeUploadsPath(rebuilt);
  } catch {
    return "";
  }
}

function appendCacheBust(url, cacheBust) {
  const normalizedUrl = safeTrim(url);
  const normalizedCacheBust = safeTrim(cacheBust);
  if (!normalizedUrl || !normalizedCacheBust) return normalizedUrl;

  const joiner = normalizedUrl.includes("?") ? "&" : "?";
  return `${normalizedUrl}${joiner}v=${encodeURIComponent(normalizedCacheBust)}`;
}

export function normalizeApiBase(apiBase = DEFAULT_API_BASE) {
  return safeTrim(apiBase).replace(/\/$/, "");
}

export function isAbsoluteHttpUrl(value = "") {
  return /^https?:\/\//i.test(safeTrim(value));
}

export function buildEndpointUrls(apiBase, apiPath, nonApiPath) {
  const normalizedApiPath = String(apiPath || "").startsWith("/") ? apiPath : `/${apiPath || ""}`;
  const normalizedNonApiPath = String(nonApiPath || "").startsWith("/")
    ? nonApiPath
    : `/${nonApiPath || ""}`;
  const base = normalizeApiBase(apiBase);
  const usesApiBase = /\/api$/i.test(base);
  const rootBase = usesApiBase ? base.replace(/\/api$/i, "") : base;

  if (!base) {
    return dedupeUrls([normalizedApiPath, normalizedNonApiPath]);
  }

  if (isAbsoluteHttpUrl(base)) {
    return dedupeUrls([
      `${base}${usesApiBase ? normalizedNonApiPath : normalizedApiPath}`,
    ]);
  }

  const candidates = [
    `${base}${usesApiBase ? normalizedNonApiPath : normalizedApiPath}`,
    rootBase && rootBase !== base ? `${rootBase}${normalizedApiPath}` : "",
    rootBase && rootBase !== base ? `${rootBase}${normalizedNonApiPath}` : "",
    normalizedApiPath,
    normalizedNonApiPath,
  ];

  return dedupeUrls(candidates);
}

export function buildPublicBase(apiBase = DEFAULT_API_BASE) {
  const base = normalizeApiBase(apiBase);
  const windowOrigin = buildWindowOrigin();

  if (!base) return windowOrigin;
  if (base === "/api") return windowOrigin;

  if (isAbsoluteHttpUrl(base)) {
    return base.endsWith("/api") ? base.slice(0, -4) : base;
  }

  if (base.endsWith("/api")) {
    return joinUrl(windowOrigin, base.slice(0, -4));
  }

  return joinUrl(windowOrigin, base);
}

export function shouldTryEndpointFallbackStatus(status) {
  return [404, 405, 502, 503, 504].includes(Number(status));
}

export function buildApiProxyUrl(pathname, apiBase = DEFAULT_API_BASE) {
  const normalizedPath = String(pathname || "").startsWith("/")
    ? pathname
    : `/${pathname || ""}`;
  const base = normalizeApiBase(apiBase);
  const windowOrigin = buildWindowOrigin();

  if (!base) {
    return `${joinUrl(windowOrigin, "/api")}${normalizedPath}`;
  }

  if (isAbsoluteHttpUrl(base)) {
    return `${/\/api$/i.test(base) ? base : `${base}/api`}${normalizedPath}`;
  }

  const relativeApiBase = /\/api$/i.test(base) ? base : `${base}/api`;
  return `${joinUrl(windowOrigin, relativeApiBase)}${normalizedPath}`;
}

export function resolvePublicAssetUrl(
  value,
  {
    apiBase = DEFAULT_API_BASE,
    preferApiProxyForUploads = false,
    cacheBust = "",
  } = {}
) {
  const raw = safeTrim(value);
  if (!raw) return "";
  if (/^data:/i.test(raw)) return raw;

  const uploadsPath = extractUploadsPath(raw);
  if (preferApiProxyForUploads && uploadsPath) {
    return appendCacheBust(buildApiProxyUrl(uploadsPath, apiBase), cacheBust);
  }

  if (raw.startsWith("//")) {
    const protocol = typeof window !== "undefined" && window.location?.protocol
      ? window.location.protocol
      : "https:";
    return appendCacheBust(`${protocol}${raw}`, cacheBust);
  }

  try {
    const parsed = new URL(raw);
    const host = safeTrim(parsed.hostname).toLowerCase();

    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") {
      const rebuiltPath = `${parsed.pathname || ""}${parsed.search || ""}${parsed.hash || ""}`;
      return appendCacheBust(joinUrl(buildPublicBase(apiBase), rebuiltPath), cacheBust);
    }

    return appendCacheBust(parsed.toString(), cacheBust);
  } catch {
    if (uploadsPath) {
      return appendCacheBust(joinUrl(buildPublicBase(apiBase), uploadsPath), cacheBust);
    }

    if (raw.startsWith("/")) {
      return appendCacheBust(joinUrl(buildPublicBase(apiBase), raw), cacheBust);
    }

    return appendCacheBust(joinUrl(buildPublicBase(apiBase), `/${raw.replace(/^\/+/, "")}`), cacheBust);
  }
}
